/**
 * Reading a stored offer back.
 *
 * The offer card owns the offer now, so a stored offer names its kind and keeps
 * its link under `url`. An offer stored before that was always a call and kept
 * its link under `booking_url`, so it is read as one. Only the new shape is
 * ever written.
 */
import type { BoardCards, OfferJson, OfferKind, OfferVideo } from "./types";

/** How long an offer that opens by itself stays open before it folds to its tab: a recorded room, and a live. */
export const OFFER_FOLD_MS = { room: 20_000, live: 16_000 } as const;

/** True when the offer opens a link and has none a guest could open: the one rule Publish and the walk both use. */
export function offerNeedsLink(offer: Pick<BoardCards["offer"], "offerType" | "url">): boolean {
  return (offer.offerType === "call" || offer.offerType === "buy") && !/^https?:\/\/\S+\.\S+/.test(offer.url);
}

const KINDS: readonly string[] = ["call", "buy", "code", "download", "message"];

/** The box's presets. Absent means the page's own: the brand accent, as it always was. */
const OFFER_LOOKS = ["wine", "paper", "ink", "brand"] as const;
const HEX = /^#[0-9a-f]{6}$/;

/** A look as stored or typed: a preset key, or the host's own colour; anything else is no look. */
export function readLook(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const v = raw.trim().toLowerCase();
  if ((OFFER_LOOKS as readonly string[]).includes(v)) return v;
  return HEX.test(v) ? v : undefined;
}

export function isCustomLook(look: string | undefined): look is string {
  return typeof look === "string" && HEX.test(look);
}

const NOTE_ID = /^note_[a-z0-9]{6,40}$/;

function readVideo(raw: unknown): OfferVideo | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const v = raw as Record<string, unknown>;
  const url = text(v.url);
  if (!url) return undefined;
  return {
    url,
    poster: text(v.poster) || null,
    duration_sec: typeof v.duration_sec === "number" && Number.isFinite(v.duration_sec) ? v.duration_sec : 0,
  };
}

/** The offer with its video resolved, when there is one to play. */
export function withOfferVideo(offer: OfferJson | null, video: OfferVideo | null | undefined): OfferJson | null {
  if (!offer) return null;
  return video ? { ...offer, video } : offer;
}

/**
 * The tag the host's own product sends when a person has signed up. One name, so
 * the stop rule, the ingest API and the words on the offer card all mean the
 * same thing. A press of the offer button is a click, never this.
 */
export const CUSTOMER_TAG = "customer";

function text(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/** One stored offer, in the shape the room and the detail page read. */
export function readOffer(raw: unknown): OfferJson | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const kind = (KINDS.includes(text(o.kind)) ? o.kind : "call") as OfferKind;
  const url = text(o.url) || text(o.booking_url);
  const code = text(o.code);
  const file = text(o.file);
  const words = text(o.text);
  const look = readLook(o.look);
  const note = text(o.video_note);
  const video = readVideo(o.video);
  return {
    kind,
    label: text(o.label),
    ...(url ? { url } : {}),
    ...(code ? { code } : {}),
    ...(file ? { file } : {}),
    ...(words ? { text: words } : {}),
    ...(look ? { look } : {}),
    ...(NOTE_ID.test(note) ? { video_note: note } : {}),
    ...(video ? { video } : {}),
    ...(typeof o.appears_at === "number" && Number.isFinite(o.appears_at)
      ? { appears_at: o.appears_at }
      : {}),
    ...(showsFor(o.shows_for) ? { shows_for: showsFor(o.shows_for) } : {}),
  };
}

/** The offer's window as stored: whole seconds, at least half a minute; anything else is "to the end". */
function showsFor(raw: unknown): number | undefined {
  return typeof raw === "number" && Number.isFinite(raw) && raw >= 30 ? Math.round(raw) : undefined;
}

/** Has the offer's window passed? An offer with no window stays to the end. */
export function offerClosed(offer: OfferJson, watchedSec: number): boolean {
  const span = offer.shows_for;
  if (typeof span !== "number" || span <= 0) return false;
  const at = typeof offer.appears_at === "number" && offer.appears_at > 0 ? offer.appears_at : 0;
  return watchedSec >= at + span;
}

/** Has the talk reached the offer? An offer with no time is there from the start. */
export function offerArrived(offer: OfferJson, watchedSec: number): boolean {
  const at = offer.appears_at;
  if (typeof at !== "number" || at <= 0) return true;
  return watchedSec >= at;
}

/**
 * The offer one card publishes, kind by kind. The room, the board's stage and
 * the publish path all read the card through this, so what the host sets on the
 * card is what the viewer meets.
 */
export function offerFrom(card: BoardCards["offer"]): OfferJson {
  const kind = card.offerType;
  const url = text(card.url);
  const code = text(card.code);
  const file = text(card.file);
  const words = text(card.text);
  const look = readLook(card.look);
  const note = text(card.videoNote);
  return {
    kind,
    // A message has no button, so it has no button words either.
    label: kind === "message" ? "" : text(card.btn) || "Book a call",
    ...(url && kind !== "download" && kind !== "message" ? { url } : {}),
    ...(kind === "code" && code ? { code } : {}),
    ...(kind === "download" && file ? { file } : {}),
    ...(words ? { text: words } : {}),
    ...(look ? { look } : {}),
    ...(NOTE_ID.test(note) ? { video_note: note } : {}),
    ...(typeof card.t === "number" ? { appears_at: card.t } : {}),
    ...(showsFor(card.showFor) ? { shows_for: showsFor(card.showFor) } : {}),
  };
}
